import React from 'react';
import { 
  Home, MessageSquare, FileText, Calculator, Settings, CreditCard, 
  Shield, Bell, BookOpen, UserCog, Sparkles, ExternalLink 
} from 'lucide-react';
import { PageContext, PageType } from '@/types/pageContext';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface PageContextIndicatorProps {
  pageContext: PageContext;
}

const getPageTypeIcon = (pageType: PageType) => {
  const iconMap: Record<PageType, React.ComponentType<{ className?: string }>> = {
    dashboard: Home,
    chat: MessageSquare,
    documents: FileText,
    calculators: Calculator,
    settings: Settings,
    plans: CreditCard,
    legal: Shield,
    notifications: Bell,
    tutorials: BookOpen,
    admin: UserCog,
    home: Home,
    unknown: Sparkles
  };
  
  return iconMap[pageType] || Sparkles;
};

const getPageTypeColor = (pageType: PageType) => {
  const colorMap: Record<PageType, string> = {
    dashboard: 'bg-blue-500/10 text-blue-700 border-blue-200',
    chat: 'bg-green-500/10 text-green-700 border-green-200',
    documents: 'bg-purple-500/10 text-purple-700 border-purple-200',
    calculators: 'bg-orange-500/10 text-orange-700 border-orange-200',
    settings: 'bg-gray-500/10 text-gray-700 border-gray-200',
    plans: 'bg-yellow-500/10 text-yellow-700 border-yellow-200',
    legal: 'bg-red-500/10 text-red-700 border-red-200',
    notifications: 'bg-pink-500/10 text-pink-700 border-pink-200',
    tutorials: 'bg-indigo-500/10 text-indigo-700 border-indigo-200',
    admin: 'bg-slate-500/10 text-slate-700 border-slate-200',
    home: 'bg-teal-500/10 text-teal-700 border-teal-200',
    unknown: 'bg-muted text-muted-foreground border-border'
  };
  
  return colorMap[pageType] || colorMap.unknown;
};

const getPageDisplayName = (pageType: PageType) => {
  const displayNames: Record<PageType, string> = {
    dashboard: 'Dashboard',
    chat: 'Chat IA',
    documents: 'Documentos',
    calculators: 'Calculadoras',
    settings: 'Configurações',
    plans: 'Planos',
    legal: 'Legal',
    notifications: 'Notificações',
    tutorials: 'Tutoriais',
    admin: 'Admin',
    home: 'Início',
    unknown: 'Página'
  };
  
  return displayNames[pageType] || 'Página';
};

export const PageContextIndicator: React.FC<PageContextIndicatorProps> = ({ pageContext }) => {
  const Icon = getPageTypeIcon(pageContext.pageType);
  const colorClass = getPageTypeColor(pageContext.pageType);
  const displayName = getPageDisplayName(pageContext.pageType);
  
  const formatUrl = (url: string) => {
    if (url === '/') return '/painel';
    return url.length > 25 ? `${url.substring(0, 22)}...` : url;
  };

  return (
    <div className="space-y-2">
      {/* Main Context Badge */}
      <div className={cn(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border",
        colorClass
      )}>
        <Icon className="w-3.5 h-3.5" />
        <span>{displayName}</span>
        <ExternalLink className="w-3 h-3 opacity-60" />
      </div>

      {/* Page Details */}
      <div className="flex flex-wrap gap-1 text-xs">
        <Badge variant="outline" className="text-xs h-5 px-2">
          {formatUrl(pageContext.url)}
        </Badge>
        
        {pageContext.features.length > 0 && (
          <Badge variant="outline" className="text-xs h-5 px-2">
            {pageContext.features.length} recurso(s)
          </Badge>
        )}
        
        {pageContext.forms.length > 0 && (
          <Badge variant="outline" className="text-xs h-5 px-2">
            {pageContext.forms.length} formulário(s)
          </Badge>
        )}
      </div>

      {/* Title if different from type */}
      {pageContext.title && 
       pageContext.title !== displayName && 
       pageContext.title !== 'Página Atual' && (
        <div className="text-xs text-muted-foreground truncate">
          {pageContext.title}
        </div>
      )}
    </div>
  );
};